import React, { useEffect, useState } from "react";
import api from "../api";
import { PageHeader, StatusBadge, inr } from "../components/UI";
import { Link } from "react-router-dom";
import { Users, ShoppingCart, IndianRupee, Clock, TrendingUp, Award, Sparkles, ArrowRight, Target, CheckSquare, AlertCircle, Phone } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

function StatCard({ label, value, hint, icon: Icon, accent = "#C1121F", testid }) {
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-[#475569]">{label}</div>
        <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: `${accent}15` }}>
          <Icon size={18} style={{ color: accent }} strokeWidth={1.75} />
        </div>
      </div>
      <div className="font-display font-bold text-3xl tracking-tight text-[#0F172A]">{value}</div>
      {hint && <div className="text-xs text-[#94A3B8] mt-2">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [reorderSummary, setReorderSummary] = useState(null);
  const [myStats, setMyStats] = useState(null);
  const [digest, setDigest] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [churn, setChurn] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {}),
      api.get("/reorder/predictions").then((r) => setReorderSummary(r.data.summary)).catch(() => {}),
      api.get("/staff/my-stats").then((r) => setMyStats(r.data)).catch(() => {}),
      api.get("/digest/today").then((r) => setDigest(r.data)).catch(() => {}),
      api.get("/forecast/next-month").then((r) => setForecast(r.data)).catch(() => {}),
      api.get("/churn/alerts").then((r) => setChurn(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="p-10 text-[#475569]" data-testid="dashboard-loading">Loading the press floor…</div>;
  }

  return (
    <div className="p-4 lg:p-8 max-w-7xl">
      <PageHeader title="Dashboard" subtitle="Today's overview of your printing operations" />

      {/* AI Forecast banner */}
      {forecast && forecast.projected_revenue > 0 && (
        <div className="mb-5 card-flat p-5 bg-gradient-to-br from-[#0F172A] to-[#1E293B] text-white border-0" data-testid="forecast-card">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center backdrop-blur-sm">
              <TrendingUp size={22} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-white/70">🎯 AI Forecast · {forecast.next_month_label}</div>
                <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase ${
                  forecast.confidence === "high" ? "bg-green-500/20 text-green-300" :
                  forecast.confidence === "medium" ? "bg-amber-500/20 text-amber-300" :
                  "bg-slate-500/20 text-slate-300"
                }`}>
                  {forecast.confidence} confidence
                </span>
              </div>
              <div className="flex items-baseline gap-3">
                <div className="font-display font-bold text-3xl font-mono-num">{inr(forecast.projected_revenue)}</div>
                <div className="text-xs text-white/60">projected</div>
              </div>
              {forecast.narrative && (
                <div className="mt-3 text-sm text-white/80 leading-relaxed">{forecast.narrative}</div>
              )}
              <div className="mt-3 flex gap-4 text-[11px] text-white/60">
                <span>3-mo avg: <span className="font-mono-num text-white/90">{inr(forecast.base_3mo_avg)}</span></span>
                <span>Seasonality: <span className="font-mono-num text-white/90">{forecast.season_factor}×</span></span>
                <span>Trend: <span className="font-mono-num text-white/90">{forecast.trend_factor}×</span></span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Churn alert */}
      {churn && churn.total > 0 && (
        <Link to="/churn" className="block mb-5" data-testid="churn-alert-banner">
          <div className="card-flat p-4 border-l-4 border-l-red-500 flex items-center justify-between hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle size={18} className="text-red-600"/>
              </div>
              <div>
                <div className="font-display font-bold text-base">⚠️ {churn.total} customer{churn.total !== 1 ? "s" : ""} at churn risk</div>
                <div className="text-sm text-[#475569]">
                  {churn.critical > 0 && <span className="text-red-600 font-semibold">{churn.critical} critical</span>}
                  {churn.critical > 0 && churn.warning > 0 && " · "}
                  {churn.warning > 0 && <span className="text-amber-600 font-semibold">{churn.warning} warning</span>}
                  {" "}— stopped ordering for 90+ days. Win them back.
                </div>
              </div>
            </div>
            <ArrowRight size={18} className="text-[#475569]"/>
          </div>
        </Link>
      )}

      {reorderSummary && (reorderSummary.overdue > 0 || reorderSummary.urgent > 0) && (
        <Link to="/reorders" className="block mb-5" data-testid="reorder-alert-banner">
          <div className="p-4 bg-gradient-to-r from-[#C1121F] to-[#E07A5F] text-white rounded-lg flex items-center justify-between hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                <Sparkles size={20} />
              </div>
              <div>
                <div className="font-display font-bold text-lg leading-tight">
                  {(reorderSummary.overdue || 0) + (reorderSummary.urgent || 0)} customer{((reorderSummary.overdue || 0) + (reorderSummary.urgent || 0)) !== 1 ? "s" : ""} ready to reorder
                </div>
                <div className="text-sm text-white/90 mt-0.5">
                  AI predicts these customers&apos; stock is running low. Call now to lock in repeat business.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm font-semibold">
              Open Reorder Radar <ArrowRight size={16} />
            </div>
          </div>
        </Link>
      )}

      {/* My Targets + Today's Plan */}
      {(myStats || digest) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6" data-testid="my-targets-row">
          {myStats && (
            <div className="card-flat p-5 lg:col-span-2" data-testid="my-targets">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Target size={18} className="text-[#C1121F]"/>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-[#475569]">My Progress · {myStats.month}</div>
                    <h3 className="font-display font-bold text-lg">{myStats.user_name}</h3>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex justify-between text-xs uppercase tracking-wider font-semibold text-[#475569]">
                    <span>Sales</span>
                    <span className="text-[#C1121F]">{myStats.sales.pct}%</span>
                  </div>
                  <div className="h-2 bg-[#F1F5F9] rounded-full mt-1.5 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#C1121F] to-[#E07A5F] transition-all" style={{ width: `${Math.min(100, myStats.sales.pct)}%` }} />
                  </div>
                  <div className="flex justify-between mt-1.5 text-xs">
                    <span className="font-mono-num font-semibold">{inr(myStats.sales.achieved)}</span>
                    <span className="text-[#94A3B8] font-mono-num">of {inr(myStats.sales.target)}</span>
                  </div>
                  {myStats.sales.target > 0 && (
                    <div className="text-[10px] text-[#475569] mt-1">
                      {myStats.sales.pct >= 100 ? "🎉 Target achieved!" : `${inr(myStats.sales.remaining)} remaining`}
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex justify-between text-xs uppercase tracking-wider font-semibold text-[#475569]">
                    <span>Collection</span>
                    <span className="text-green-700">{myStats.collection.pct}%</span>
                  </div>
                  <div className="h-2 bg-[#F1F5F9] rounded-full mt-1.5 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-green-600 to-green-500 transition-all" style={{ width: `${Math.min(100, myStats.collection.pct)}%` }} />
                  </div>
                  <div className="flex justify-between mt-1.5 text-xs">
                    <span className="font-mono-num font-semibold">{inr(myStats.collection.achieved)}</span>
                    <span className="text-[#94A3B8] font-mono-num">of {inr(myStats.collection.target)}</span>
                  </div>
                  {myStats.collection.target > 0 && (
                    <div className="text-[10px] text-[#475569] mt-1">
                      {myStats.collection.pct >= 100 ? "🎉 Target achieved!" : `${inr(myStats.collection.remaining)} remaining`}
                    </div>
                  )}
                </div>
              </div>
              {myStats.sales.target === 0 && myStats.collection.target === 0 && (
                <div className="mt-4 p-3 bg-[#F8F9FA] rounded text-xs text-[#475569]">
                  No targets set yet. Admin can set monthly targets from the <Link to="/users" className="text-[#C1121F] font-semibold">Team page</Link>.
                </div>
              )}
            </div>
          )}

          {digest && (
            <Link to="/tasks" className="card-flat p-5 hover:shadow-lg transition-shadow" data-testid="todays-plan">
              <div className="flex items-center gap-2 mb-3">
                <CheckSquare size={18} className="text-[#C1121F]"/>
                <div className="text-[11px] uppercase tracking-wider font-semibold text-[#475569]">Today&apos;s Plan</div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 rounded bg-[#F8F9FA]">
                  <div className="flex items-center gap-2">
                    <CheckSquare size={14} className="text-[#475569]"/>
                    <span className="text-sm">Tasks</span>
                  </div>
                  <span className="font-mono-num font-bold">{digest.counts.tasks}</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded bg-[#FEF2F2]">
                  <div className="flex items-center gap-2">
                    <Phone size={14} className="text-[#C1121F]"/>
                    <span className="text-sm">Order Follow-ups</span>
                  </div>
                  <span className="font-mono-num font-bold text-[#C1121F]">{digest.counts.order_followups || 0}</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded bg-[#FEF2F2]">
                  <div className="flex items-center gap-2">
                    <Phone size={14} className="text-[#C1121F]"/>
                    <span className="text-sm">Reorder Calls</span>
                  </div>
                  <span className="font-mono-num font-bold text-[#C1121F]">{digest.counts.calls}</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded bg-amber-50">
                  <div className="flex items-center gap-2">
                    <AlertCircle size={14} className="text-amber-600"/>
                    <span className="text-sm">Payment Follow-ups</span>
                  </div>
                  <span className="font-mono-num font-bold text-amber-700">{digest.counts.payment_followups}</span>
                </div>
              </div>
              <div className="mt-3 text-xs text-[#C1121F] font-semibold flex items-center gap-1">
                Open To-Do <ArrowRight size={12}/>
              </div>
            </Link>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 stagger" data-testid="stats-grid">
        <StatCard testid="stat-customers" label="Customers" value={stats.total_customers} icon={Users} hint="Hospitals, schools & presses" />
        <StatCard testid="stat-orders" label="Total Orders" value={stats.total_orders} icon={ShoppingCart} accent="#E07A5F" hint={`${stats.pending_orders} pending · ${stats.printing_orders} printing`} />
        <StatCard testid="stat-revenue" label="Total Revenue" value={inr(stats.total_revenue)} icon={IndianRupee} accent="#10B981" hint={`Paid: ${inr(stats.total_paid)}`} />
        <StatCard testid="stat-due" label="Pending Payments" value={inr(stats.total_due)} icon={Clock} accent="#F59E0B" hint="Across all invoices" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="card-flat p-5 lg:col-span-2" data-testid="revenue-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-[#475569]">Revenue · Last 6 Months</div>
              <h3 className="font-display font-bold text-xl mt-1">Monthly performance</h3>
            </div>
            <TrendingUp size={20} className="text-[#C1121F]" />
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.monthly_revenue} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="month" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `₹${v >= 1000 ? (v/1000).toFixed(0) + 'k' : v}`} />
              <Tooltip
                cursor={{ fill: "rgba(193, 18, 31, 0.06)" }}
                contentStyle={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 12 }}
                formatter={(v) => inr(v)}
              />
              <Bar dataKey="revenue" fill="#C1121F" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card-flat p-5" data-testid="top-customers">
          <div className="flex items-center gap-2 mb-4">
            <Award size={18} className="text-[#C1121F]" />
            <div className="text-[11px] uppercase tracking-wider font-semibold text-[#475569]">Top Customers</div>
          </div>
          {stats.top_customers.length === 0 ? (
            <div className="text-sm text-[#94A3B8] py-8 text-center">No customers yet</div>
          ) : (
            <div className="space-y-3">
              {stats.top_customers.map((c, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded bg-[#FEF2F2] text-[#C1121F] font-mono-num font-bold text-xs flex items-center justify-center">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{c.name}</div>
                    <div className="text-xs text-[#94A3B8] font-mono-num">{inr(c.revenue)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card-flat mt-6 overflow-hidden" data-testid="recent-orders">
        <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-[#475569]">Activity</div>
            <h3 className="font-display font-bold text-xl mt-1">Recent orders</h3>
          </div>
        </div>
        {stats.recent_orders.length === 0 ? (
          <div className="p-8 text-sm text-[#94A3B8] text-center">No orders yet. Create your first job from the Orders page.</div>
        ) : (
          <table className="crm-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Items</th>
                <th className="text-right">Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent_orders.map((o) => (
                <tr key={o.id}>
                  <td className="font-mono-num font-semibold text-[#C1121F]">{o.order_no}</td>
                  <td>{o.customer_name}</td>
                  <td className="text-[#475569]">{o.items.length} item{o.items.length !== 1 ? 's' : ''}</td>
                  <td className="text-right font-mono-num">{inr(o.total)}</td>
                  <td><StatusBadge status={o.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
